import type { PixelCoord } from '../../shared/hex';
import type { GameState, PlayerId } from '../../shared/types/domain';
import type { AnimationState } from './animation';
import {
  buildShipLabelView,
  getDisabledShipLabel,
  getShipIdentityMarker,
  shouldShowLandedIndicator,
  shouldShowOrbitIndicator,
} from './entities';
import { scaledFont } from './text';
import { screenDash, screenLineWidth } from './visibility';

type DrawIdentityMarkersInput = {
  ctx: CanvasRenderingContext2D;
  ship: GameState['ships'][number];
  playerId: PlayerId;
  state: GameState;
  pos: PixelCoord;
  animState: AnimationState | null;
  zoom: number;
};

type DrawOrbitAndLandedRingsInput = {
  ctx: CanvasRenderingContext2D;
  ship: GameState['ships'][number];
  pos: PixelCoord;
  now: number;
  inGravity: boolean;
  animState: AnimationState | null;
  zoom: number;
};

type DrawShipLabelsInput = {
  ctx: CanvasRenderingContext2D;
  ship: GameState['ships'][number];
  playerId: PlayerId;
  pos: PixelCoord;
  labelYOffset: number;
  inGravity: boolean;
  animState: AnimationState | null;
  zoom: number;
};

export const drawDisabledShipBadge = (
  ctx: CanvasRenderingContext2D,
  ship: GameState['ships'][number],
  pos: PixelCoord,
  animState: AnimationState | null,
  zoom: number,
): void => {
  const disabledLabel = getDisabledShipLabel(ship, animState !== null);

  if (!disabledLabel) return;

  ctx.font = scaledFont('bold 11px Inter, sans-serif', zoom);
  ctx.textAlign = 'left';
  const labelX = pos.x + 12;
  const labelY = pos.y - 12;
  const metrics = ctx.measureText(disabledLabel);
  const iz = 1 / zoom;
  const pad = 3 * iz;
  const textH = 10 * iz;

  ctx.fillStyle = 'rgba(180, 20, 20, 0.6)';
  ctx.beginPath();
  ctx.roundRect(
    labelX - pad,
    labelY - textH - pad,
    metrics.width + pad * 2,
    textH + pad * 2,
    3 * iz,
  );
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.fillText(disabledLabel, labelX, labelY);
};

export const drawIdentityMarkers = ({
  ctx,
  ship,
  playerId,
  state,
  pos,
  animState,
  zoom,
}: DrawIdentityMarkersInput): void => {
  const identityMarker = getShipIdentityMarker(
    ship,
    playerId,
    Boolean(state.scenarioRules.hiddenIdentityInspection),
    animState !== null,
  );

  if (identityMarker === null) return;

  switch (identityMarker) {
    case 'friendlyFugitive':
      ctx.fillStyle = 'rgba(255, 215, 0, 0.9)';
      ctx.font = scaledFont('bold 11px monospace', zoom);
      ctx.textAlign = 'center';
      ctx.fillText('\u2605', pos.x, pos.y - 14);
      break;

    case 'enemyFugitive':
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255, 120, 120, 0.95)';
      ctx.font = scaledFont('bold 11px monospace', zoom);
      ctx.fillText('\u2605', pos.x, pos.y - 14);
      break;

    case 'enemyDecoy':
      ctx.textAlign = 'center';
      ctx.strokeStyle = 'rgba(220, 220, 220, 0.9)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y - 14, 4, 0, Math.PI * 2);
      ctx.stroke();
      break;
    default: {
      const _exhaustive: never = identityMarker;
      void _exhaustive;
    }
  }
};

export const drawOrbitAndLandedRings = ({
  ctx,
  ship,
  pos,
  now,
  inGravity,
  animState,
  zoom,
}: DrawOrbitAndLandedRingsInput): void => {
  if (shouldShowOrbitIndicator(ship, inGravity, animState !== null)) {
    const phase = now / 2000 + pos.x * 0.01;

    ctx.save();
    ctx.strokeStyle = 'rgba(170, 220, 255, 0.58)';
    ctx.lineWidth = screenLineWidth(1.35, zoom);
    ctx.shadowColor = 'rgba(120, 190, 255, 0.35)';
    ctx.shadowBlur = 4;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 16, phase, phase + Math.PI * 1.5);
    ctx.stroke();
    ctx.restore();
  }

  if (shouldShowLandedIndicator(ship, animState !== null)) {
    ctx.save();
    ctx.strokeStyle = 'rgba(140, 235, 130, 0.7)';
    ctx.lineWidth = screenLineWidth(1.35, zoom);
    ctx.setLineDash(screenDash([2, 2], zoom));
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 12, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }
};

export const drawShipLabels = ({
  ctx,
  ship,
  playerId,
  pos,
  labelYOffset,
  inGravity,
  animState,
  zoom,
}: DrawShipLabelsInput): void => {
  const labelView = buildShipLabelView(
    ship,
    playerId,
    inGravity,
    animState !== null,
  );

  if (!labelView) return;

  ctx.textAlign = 'center';
  ctx.fillStyle = labelView.typeColor;
  ctx.font = scaledFont(labelView.typeFont, zoom);
  ctx.fillText(labelView.typeName, pos.x, pos.y + labelYOffset);

  if (labelView.statusTag && labelView.statusColor && labelView.statusFont) {
    ctx.fillStyle = labelView.statusColor;
    ctx.font = scaledFont(labelView.statusFont, zoom);
    ctx.fillText(labelView.statusTag, pos.x, pos.y + labelYOffset + 9);
  }
};
